// DraftManagerModal — shared "Saved Drafts" browser for the booking and
// receipt create forms. Presentational: the host form owns the single
// useDraftManager instance and passes its API down as props so there is
// exactly one auto-save loop per form.
//
// Rows show the draft number, created/updated timestamps and a preview line
// (the stored title — customer name for bookings, amount for receipts). Each
// row has Load (replaces the form, with a dirty-confirm) and Delete (inline
// Yes/No confirm). The footer saves the current form state as a new draft.

import { useEffect, useState } from 'react';
import { Modal } from '../ui/Modal/Modal';
import { Button } from '../ui/Button/Button';
import type { DraftSummary } from '../../types/api';
import type { ManagedDraftType } from '../../hooks/useDraftManager';
import styles from './DraftManagerModal.module.css';

export interface DraftManagerModalProps {
  open: boolean;
  onClose: () => void;
  draftType: ManagedDraftType;
  drafts: DraftSummary[];
  /** True when the current form has unsaved content (drives the load confirm). */
  isDirty: boolean;
  /** True while the footer "Save current as draft" action is in flight. */
  isSaving: boolean;
  /** Re-fetch the draft list (called on open and after save/delete). */
  onRefresh: () => Promise<DraftSummary[]> | void;
  /** Load a draft by enc_id into the form. */
  onLoad: (encId: string) => Promise<void> | void;
  /** Delete a draft by enc_id. */
  onDelete: (encId: string) => Promise<void> | void;
  /** Save the current form state as a new draft. */
  onSaveCurrent: () => Promise<void> | void;
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function DraftManagerModal({
  open,
  onClose,
  draftType,
  drafts,
  isDirty,
  isSaving,
  onRefresh,
  onLoad,
  onDelete,
  onSaveCurrent,
}: DraftManagerModalProps): JSX.Element {
  // enc_id pending an inline delete confirmation.
  const [confirmDeleteEnc, setConfirmDeleteEnc] = useState<string | null>(null);
  // enc_id pending the dirty-load confirmation.
  const [confirmLoadEnc, setConfirmLoadEnc] = useState<string | null>(null);

  // Refresh the list each time the modal opens; reset transient inline state.
  useEffect(() => {
    if (!open) return;
    setConfirmDeleteEnc(null);
    setConfirmLoadEnc(null);
    // onRefresh identity is owned by the host hook; we only want this on open.
    void onRefresh();
  }, [open]);

  async function doLoad(encId: string) {
    await onLoad(encId);
    onClose();
  }

  function handleLoadClick(encId: string) {
    if (isDirty) {
      setConfirmLoadEnc(encId);
    } else {
      void doLoad(encId);
    }
  }

  async function handleDelete(encId: string) {
    await onDelete(encId);
    setConfirmDeleteEnc(null);
  }

  const label = draftType === 'booking' ? 'booking' : 'receipt';

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Saved Drafts"
      size="lg"
      footer={
        <Button
          variant="primary"
          loading={isSaving}
          onClick={() => void onSaveCurrent()}
        >
          Save current as draft
        </Button>
      }
    >
      <div className={styles.container}>
        {drafts.length === 0 ? (
          <p className={styles.empty}>No saved {label} drafts.</p>
        ) : (
          <ul className={styles.list}>
            {drafts.map((draft, index) => {
              const isDeleting = confirmDeleteEnc === draft.enc_id;
              const isConfirmingLoad = confirmLoadEnc === draft.enc_id;
              return (
                <li key={draft.enc_id} className={styles.row}>
                  <div className={styles.meta}>
                    <span className={styles.title}>
                      {`Draft ${index + 1}`}
                      {draft.title ? ` — ${draft.title}` : ''}
                    </span>
                    <span className={styles.dates}>
                      Created {formatDate(draft.created_at)} · Updated{' '}
                      {formatDate(draft.updated_at)}
                    </span>
                  </div>

                  <div className={styles.actions}>
                    {isConfirmingLoad ? (
                      <span className={styles.confirm} role="alert">
                        Unsaved changes will be lost. Continue?
                        <Button
                          variant="primary"
                          size="sm"
                          onClick={() => void doLoad(draft.enc_id)}
                        >
                          Yes, load
                        </Button>
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => setConfirmLoadEnc(null)}
                        >
                          No
                        </Button>
                      </span>
                    ) : isDeleting ? (
                      <span className={styles.confirm} role="alert">
                        Delete this draft?
                        <Button
                          variant="danger"
                          size="sm"
                          onClick={() => void handleDelete(draft.enc_id)}
                        >
                          Yes
                        </Button>
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => setConfirmDeleteEnc(null)}
                        >
                          No
                        </Button>
                      </span>
                    ) : (
                      <>
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => handleLoadClick(draft.enc_id)}
                        >
                          Load
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setConfirmDeleteEnc(draft.enc_id)}
                        >
                          Delete
                        </Button>
                      </>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </Modal>
  );
}
